/**
 * BusinessProvisiones — "Provisiones": qué guardar, para cuándo, y si alcanza.
 * El nombre es el contable real (Iván: "contablemente serían provisiones").
 *
 * Esta pestaña responde UNA pregunta: ¿voy a llegar a mis fechas de pago?
 * Semáforo de cobertura + recorrido obligación por obligación + el reparto de
 * cada ingreso con su simulador. AQUÍ NO HAY TASAS: dónde puede trabajar la
 * plata mientras espera vive en la pestaña Rendimientos (link cruzado).
 *
 * v1 SOLO LECTURA + SIMULACIÓN: nada aquí mueve dinero.
 * Visual: .prov-ola + tokens --fx-* (glass Ola A, claro + oscuro).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { sobresGet, sobresSet, sobresSimular, saludCaja } from '../../lib/cerebro';

const fmtCop = (n) => `$${Math.round(Number(n || 0)).toLocaleString('es-CO')}`;
const fmtFecha = (d) => (d ? new Date(d).toLocaleDateString('es-CO', { day: 'numeric', month: 'short', timeZone: 'UTC' }) : null);

const COLORES = { iva: '#B45309', nomina: '#3d8bfd', prima: '#8b5cf6', contingencia: '#0E7490', libre: '#16A34A' };
const colorDe = (clave, i) => COLORES[clave] || ['#DC2626', '#DB2777', '#65A30D'][i % 3];

const SEMAFORO = {
  verde: { tone: 'verde', label: 'Vas bien' },
  amarillo: { tone: 'amarillo', label: 'Ajustado' },
  rojo: { tone: 'rojo', label: 'Vas a quedar corto' },
};

function TarjetaSalud({ salud }) {
  if (!salud) return null;
  if (salud.ok === false) {
    return (
      <div className="prov-ola__panel">
        <div className="prov-ola__h2">¿Te alcanza para tus fechas?</div>
        <p className="prov-ola__hint">{salud.mensaje}</p>
        <Link to="/business/caja" className="prov-ola__link">Ir a Caja →</Link>
      </div>
    );
  }
  const s = SEMAFORO[salud.semaforo] || SEMAFORO.verde;
  return (
    <div className={`prov-ola__semaforo prov-ola__semaforo--${s.tone}`}>
      <div className="prov-ola__semaforo-top">
        <span className="prov-ola__dot" />
        <span className="prov-ola__semaforo-label">{s.label}</span>
      </div>
      <p className="prov-ola__semaforo-text">{salud.resumenTexto}</p>
      {salud.nota && <p className="prov-ola__semaforo-note">{salud.nota}</p>}
    </div>
  );
}

function LineaTiempo({ salud }) {
  const detalle = salud?.detalle || [];
  if (!detalle.length) return null;
  return (
    <div className="prov-ola__panel">
      <div className="prov-ola__h2">Tus próximas fechas de pago</div>
      <p className="prov-ola__hint mb">
        Con tu saldo de hoy ({fmtCop(salud.saldo)}), pagando en orden de fecha. Edita montos y fechas en{' '}
        <Link to="/business/caja" className="prov-ola__link">Caja</Link>.
      </p>
      <div className="prov-ola__timeline">
        {detalle.map((o, i) => (
          <div key={`${o.clave}-${i}`} className="prov-ola__row">
            <div className={`prov-ola__status-dot${o.cubierto ? ' is-ok' : ' is-bad'}`} />
            <div className="prov-ola__row-body">
              <div className="prov-ola__row-title">{o.nombre}</div>
              <div className="prov-ola__row-meta">
                {fmtFecha(o.fecha)} · en {o.dias} día{o.dias === 1 ? '' : 's'}
              </div>
            </div>
            <div className="prov-ola__row-right">
              <div className="prov-ola__row-amt">{fmtCop(o.monto)}</div>
              <div className={`prov-ola__row-sub${o.cubierto ? '' : ' is-bad'}`}>
                {o.cubierto ? `quedan ${fmtCop(o.saldoDespues)}` : `faltan ${fmtCop(-o.saldoDespues)}`}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function BusinessProvisiones() {
  const [sobres, setSobres] = useState(null);
  const [empresa, setEmpresa] = useState('');
  const [salud, setSalud] = useState(null);
  const [error, setError] = useState('');
  const [okMsg, setOkMsg] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [editando, setEditando] = useState(false);

  const [monto, setMonto] = useState('');
  const [sim, setSim] = useState(null);
  const [simulando, setSimulando] = useState(false);

  const cargar = useCallback(async () => {
    try {
      const cfg = await sobresGet();
      if (cfg?.sinEmpresa) { setError('Tu usuario aún no tiene una empresa vinculada.'); return; }
      setEmpresa(cfg.empresa || '');
      setSobres(cfg.sobres || []);
      setError('');
      saludCaja().then((s) => { if (s && !s.sinEmpresa) setSalud(s); }).catch(() => {});
    } catch (err) {
      if (err?.response?.status === 403) setError('FlowexPay Business está en piloto privado.');
      else setError('No pude cargar tus provisiones. Intenta de nuevo.');
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const sumaObligaciones = useMemo(
    () => (sobres || []).filter((s) => !s.esLibre).reduce((a, s) => a + (Number(s.pct) || 0), 0),
    [sobres],
  );
  const libreCalculado = Math.max(0, Math.round((100 - sumaObligaciones) * 10) / 10);
  const sobrepasado = sumaObligaciones > 100;

  const setPct = (clave, pct) => {
    const v = Math.max(0, Math.min(100, Number(pct) || 0));
    setSobres((prev) => prev.map((s) => (s.clave === clave ? { ...s, pct: v } : s)));
  };

  const guardar = async () => {
    if (guardando || sobrepasado) return;
    setGuardando(true); setOkMsg(''); setError('');
    try {
      const cfg = await sobresSet(sobres.filter((s) => !s.esLibre));
      setSobres(cfg.sobres || []);
      setEditando(false);
      setOkMsg('Listo. Desde ahora, así se reparte cada ingreso.');
    } catch {
      setError('No se pudieron guardar los cambios. Intenta de nuevo.');
    }
    setGuardando(false);
  };

  const simular = async () => {
    const m = Number(String(monto).replace(/[^0-9.]/g, ''));
    if (!m || simulando) return;
    setSimulando(true); setSim(null);
    try {
      const r = await sobresSimular(m, 'COP');
      setSim(r?.ok ? r : null);
      if (!r?.ok) setError(r?.mensaje || 'No pude simular ese monto.');
    } catch {
      setError('No pude simular. Intenta de nuevo.');
    }
    setSimulando(false);
  };

  if (error && !sobres) {
    return (
      <div className="prov-ola">
        <div className="prov-ola__banner prov-ola__banner--warn">{error}</div>
      </div>
    );
  }
  if (!sobres) {
    return (
      <div className="prov-ola">
        <div className="prov-ola__loading">Cargando tus provisiones…</div>
      </div>
    );
  }

  return (
    <div className="prov-ola">
      <header className="prov-ola__head">
        <p className="prov-ola__eyebrow">Tesorería</p>
        <h1 className="prov-ola__title">Provisiones</h1>
        <p className="prov-ola__sub">
          Qué guardar, para cuándo, y si alcanza. Cada peso que entra ya sabe para qué es — y lo que queda es
          {' '}<span className="prov-ola__libre">libre de verdad</span>.
        </p>
      </header>

      {okMsg && <div className="prov-ola__banner prov-ola__banner--ok">{okMsg}</div>}
      {error && <div className="prov-ola__banner prov-ola__banner--warn">{error}</div>}

      <TarjetaSalud salud={salud} />
      <LineaTiempo salud={salud} />

      <section className="prov-ola__panel">
        <div className="prov-ola__panel-top">
          <div className="prov-ola__h2">Cómo se reparte cada ingreso{empresa ? ` de ${empresa}` : ''}</div>
          {!editando ? (
            <button type="button" onClick={() => { setEditando(true); setOkMsg(''); }} className="prov-ola__text-btn">
              Editar
            </button>
          ) : (
            <div className="prov-ola__edit-actions">
              <button type="button" onClick={() => { setEditando(false); cargar(); }} className="prov-ola__text-btn muted">
                Cancelar
              </button>
              <button
                type="button"
                onClick={guardar}
                disabled={sobrepasado || guardando}
                className="prov-ola__btn prov-ola__btn--primary"
              >
                {guardando ? 'Guardando…' : 'Guardar'}
              </button>
            </div>
          )}
        </div>

        <div className="prov-ola__bar">
          {sobres.map((s, i) => (
            <div
              key={s.clave}
              style={{ width: `${s.esLibre ? libreCalculado : s.pct}%`, backgroundColor: colorDe(s.clave, i) }}
              title={`${s.nombre} ${s.esLibre ? libreCalculado : s.pct}%`}
            />
          ))}
        </div>

        <div className="prov-ola__legend">
          {sobres.map((s, i) => (
            <div key={s.clave} className="prov-ola__legend-row">
              <span className="prov-ola__legend-dot" style={{ backgroundColor: colorDe(s.clave, i) }} />
              <div className="prov-ola__legend-body">
                <div className="prov-ola__legend-top">
                  <div className="prov-ola__legend-name">{s.nombre}</div>
                  {editando && !s.esLibre ? (
                    <div className="prov-ola__pct-edit">
                      <input
                        type="number"
                        min="0"
                        max="100"
                        value={s.pct}
                        onChange={(e) => setPct(s.clave, e.target.value)}
                        className="prov-ola__input prov-ola__input--pct"
                      />
                      <span>%</span>
                    </div>
                  ) : (
                    <span className="prov-ola__pct" style={{ color: colorDe(s.clave, i) }}>
                      {s.esLibre ? libreCalculado : s.pct}%
                    </span>
                  )}
                </div>
                <div className="prov-ola__legend-desc">{s.descripcion}</div>
              </div>
            </div>
          ))}
        </div>

        {sobrepasado && (
          <p className="prov-ola__banner prov-ola__banner--bad mt">
            Las provisiones suman {sumaObligaciones}%: más del 100%. Baja alguna — el Libre no puede ser negativo.
          </p>
        )}
      </section>

      <section className="prov-ola__panel">
        <div className="prov-ola__h2">Si te entra un pago, ¿cómo se reparte?</div>
        <p className="prov-ola__hint mb">Escribe un monto y míralo repartido. Es una simulación: no mueve dinero.</p>
        <div className="prov-ola__sim-row">
          <input
            value={monto}
            onChange={(e) => setMonto(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && simular()}
            placeholder="Ej: 10.000.000"
            inputMode="numeric"
            className="prov-ola__input"
          />
          <button type="button" onClick={simular} disabled={simulando} className="prov-ola__btn prov-ola__btn--primary">
            {simulando ? '…' : 'Simular'}
          </button>
        </div>

        {sim && (
          <div className="prov-ola__sim-out">
            {sim.reparto.map((r, i) => (
              <div key={r.clave} className="prov-ola__sim-line">
                <div className="prov-ola__sim-left">
                  <span className="prov-ola__legend-dot sm" style={{ backgroundColor: colorDe(r.clave, i) }} />
                  <span className={r.esLibre ? 'is-libre' : ''}>{r.nombre}</span>
                </div>
                <span className={r.esLibre ? 'is-libre amt' : 'amt'}>{fmtCop(r.monto)}</span>
              </div>
            ))}
            <p className="prov-ola__hint">{sim.nota}</p>
          </div>
        )}
      </section>

      <Link to="/business/rendimientos" className="prov-ola__bridge">
        <div className="prov-ola__bridge-title">¿Y mientras espera su fecha?</div>
        <p className="prov-ola__bridge-sub">
          La plata provisionada no tiene que dormir. Mira dónde puede trabajar — tú decides →{' '}
          <strong>Rendimientos</strong>
        </p>
      </Link>
    </div>
  );
}
